import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Error,
  Assessment,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { LoanPortfolioHealth, RepaymentSchedule } from '../types';

const PortfolioHealth: React.FC = () => {
  const { t } = useTranslation();
  const [portfolioData, setPortfolioData] = useState<LoanPortfolioHealth | null>(null);
  const [repaymentData, setRepaymentData] = useState<RepaymentSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Simulate API call - replace with actual API endpoint
    const fetchPortfolioData = async () => {
      try {
        setLoading(true);
        // Mock data - replace with actual API call
        const mockPortfolioData: LoanPortfolioHealth = {
          totalLoans: 1250,
          performingLoans: 1180,
          nonPerformingLoans: 70,
          portfolioValue: 2500000000, // 2.5B ZiG
          averageCreditScore: 685,
          riskDistribution: {
            low: 750,
            medium: 350,
            high: 150,
          },
          delinquencyRate: 5.6,
          collectionRate: 94.4,
        };

        const mockRepaymentData: RepaymentSchedule[] = [
          {
            id: '1',
            loanId: 'L001',
            installmentNumber: 1,
            dueDate: '2024-01-15',
            amount: 50000,
            principal: 45000,
            interest: 5000,
            status: 'paid',
            paymentDate: '2024-01-14',
            remainingBalance: 450000,
          },
          {
            id: '2',
            loanId: 'L001',
            installmentNumber: 2,
            dueDate: '2024-02-15',
            amount: 50000,
            principal: 45000,
            interest: 5000,
            status: 'overdue',
            remainingBalance: 405000,
          },
          // Add more mock data as needed
        ];

        setPortfolioData(mockPortfolioData);
        setRepaymentData(mockRepaymentData);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch portfolio data');
        setLoading(false);
      }
    };

    fetchPortfolioData();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!portfolioData) {
    return <Alert severity="warning">No portfolio data available</Alert>;
  }

  const riskData = [
    { name: 'Low Risk', value: portfolioData.riskDistribution.low, color: '#4caf50' },
    { name: 'Medium Risk', value: portfolioData.riskDistribution.medium, color: '#ff9800' },
    { name: 'High Risk', value: portfolioData.riskDistribution.high, color: '#f44336' },
  ];

  const monthlyTrends = [
    { month: 'Jan', performing: 1150, nonPerforming: 65, disbursements: 180000000 },
    { month: 'Feb', performing: 1160, nonPerforming: 68, disbursements: 175000000 },
    { month: 'Mar', performing: 1170, nonPerforming: 70, disbursements: 190000000 },
    { month: 'Apr', performing: 1175, nonPerforming: 72, disbursements: 185000000 },
    { month: 'May', performing: 1180, nonPerforming: 70, disbursements: 200000000 },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'overdue': return 'warning';
      case 'defaulted': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Portfolio Health Dashboard
      </Typography>

      {/* Key Metrics */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Portfolio Value
                </Typography>
                <Typography variant="h5" component="div">
                  {(portfolioData.portfolioValue / 1000000).toFixed(1)}M ZiG
                </Typography>
              </Box>
              <Assessment color="primary" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Performing Loans
                </Typography>
                <Typography variant="h5" component="div">
                  {portfolioData.performingLoans}
                </Typography>
                <Typography variant="body2" color="success.main">
                  {((portfolioData.performingLoans / portfolioData.totalLoans) * 100).toFixed(1)}%
                </Typography>
              </Box>
              <CheckCircle color="success" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Non-Performing Loans
                </Typography>
                <Typography variant="h5" component="div">
                  {portfolioData.nonPerformingLoans}
                </Typography>
                <Typography variant="body2" color="error.main">
                  {portfolioData.delinquencyRate.toFixed(1)}%
                </Typography>
              </Box>
              <Warning color="warning" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Average Credit Score
                </Typography>
                <Typography variant="h5" component="div">
                  {portfolioData.averageCreditScore}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Good
                </Typography>
              </Box>
              <TrendingUp color="primary" />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Charts */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 2, flex: '1 1 400px', minWidth: '400px' }}>
          <Typography variant="h6" gutterBottom>
            Risk Distribution
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={riskData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {riskData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Paper>

        <Paper sx={{ p: 2, flex: '1 1 400px', minWidth: '400px' }}>
          <Typography variant="h6" gutterBottom>
            Monthly Trends
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="performing" stroke="#4caf50" name={t('portfolio.performingLoans')} />
              <Line type="monotone" dataKey="nonPerforming" stroke="#f44336" name={t('portfolio.nonPerformingLoans')} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* Repayment Schedule */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Repayment Schedule Overview
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Loan ID</TableCell>
                <TableCell>Installment</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Principal</TableCell>
                <TableCell>Interest</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Remaining Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {repaymentData.map((repayment) => (
                <TableRow key={repayment.id}>
                  <TableCell>{repayment.loanId}</TableCell>
                  <TableCell>{repayment.installmentNumber}</TableCell>
                  <TableCell>{new Date(repayment.dueDate).toLocaleDateString()}</TableCell>
                  <TableCell>{repayment.amount.toLocaleString('en-ZW')} ZiG</TableCell>
                  <TableCell>{repayment.principal.toLocaleString('en-ZW')} ZiG</TableCell>
                  <TableCell>{repayment.interest.toLocaleString('en-ZW')} ZiG</TableCell>
                  <TableCell>
                    <Chip
                      label={repayment.status}
                      color={getStatusColor(repayment.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{repayment.remainingBalance.toLocaleString('en-ZW')} ZiG</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Action Buttons */}
      <Box display="flex" gap={2} justifyContent="center">
        <Button
          variant="contained"
          startIcon={<BarChartIcon />}
          onClick={() => {/* Handle detailed analysis */}}
        >
          Detailed Analysis
        </Button>
        <Button
          variant="outlined"
          startIcon={<PieChartIcon />}
          onClick={() => {/* Handle risk assessment */}}
        >
          Risk Assessment
        </Button>
        <Button
          variant="outlined"
          startIcon={<Assessment />}
          onClick={() => {/* Handle regulatory reporting */}}
        >
          Regulatory Report
        </Button>
      </Box>
    </Box>
  );
};

export default PortfolioHealth;
